import type { FC, MouseEvent } from 'react';
import { isValidElement, memo } from 'react';
import {
  CatalogTileBadge,
  CatalogTile as PfCatalogTile,
} from '@patternfly/react-catalog-view-extension';
import { Badge } from '@patternfly/react-core/dist/dynamic/components/Badge';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { CatalogItem } from '@console/dynamic-plugin-sdk/src/extensions';
import { isModifiedEvent } from '../../utils/utils';
import CatalogBadges from './CatalogBadges';
import { getIconProps } from './utils/catalog-utils';
import type { CatalogType } from './utils/types';

import './CatalogTile.scss';

type CatalogTileProps = {
  item: CatalogItem;
  catalogTypes: CatalogType[];
  onClick?: (item: CatalogItem) => void;
  href?: string;
};

const CatalogTile: FC<CatalogTileProps> = ({ item, catalogTypes, onClick, href }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid, name, title, provider, description, type, typeLabel, badges } = item;
  const vendor = provider ? t('console-shared~Provided by {{provider}}', { provider }) : null;
  const catalogType = _.find(catalogTypes, ['value', type]);

  const typeBadges = [
    <CatalogTileBadge>
      <Badge isRead>{typeLabel ?? catalogType?.label}</Badge>
    </CatalogTileBadge>,
  ];

  const isDescriptionReactElement = isValidElement(description);
  return (
    <PfCatalogTile
      id={uid}
      className="odc-catalog-tile co-catalog-tile"
      onClick={(e: MouseEvent<HTMLElement>) => {
        if (isModifiedEvent(e)) return;
        e.preventDefault();
        if (onClick) {
          onClick(item);
        } else if (href) {
          navigate(href);
        }
      }}
      href={href}
      title={title || name}
      badges={typeBadges}
      vendor={vendor}
      description={isDescriptionReactElement ? undefined : description}
      data-test={`${type}-${name}`}
      {...getIconProps(item)}
    >
      {isDescriptionReactElement ? description : undefined}
      {badges?.length > 0 ? <CatalogBadges badges={badges} /> : undefined}
    </PfCatalogTile>
  );
};

export default memo<CatalogTileProps>(CatalogTile);
